import React, { useContext, useEffect, useState } from 'react';
import type { FC, PropsWithChildren } from 'react';
import axios from 'axios';

import { AppContext } from '../../capabilities/contextStore/AppContext';
import { getCheckoutSessionSettingsFromCommonCheckoutApi } from '../../services/commonCheckout/getCheckoutSessionSettingsFromCommonCheckoutApi';
import { createDefaultHeader, setHeaders } from '../../api/apis';
import { settingsApi } from '../../services/settingsApi/settingsApi';
import {
  GUEST,
  SESSION_ALREADY_PROCESSED_HTTP_STATUS,
  SESSSION_ID_INVALID_STATUS_CODE,
} from '../../utils/constants';
import { normalizeUiModesFromServer } from '../../unified-router/utils/normalizeUiModesFromServer';
import { CCGThemeProviderContext } from '../../capabilities/contextStore/CCGThemeProviderContext';
import { isSessionComplete } from '../../capabilities/checkout-v2/checkout/components/checkout-sessions-polling/utils/isSessionComplete';
import { ensureIsInstanceOfError } from '../../utils/function/ensureIsInstanceOfError';
import { isHttpErrorResponse } from '../../utils/function/isHttpErrorResponse';
import { logWidgetUsageStatsEvents } from '../../services/AppInsights/Events/log-widget-usage-stats';
import {
  getDistributionType,
  getVersion,
} from '../../build-info';
import useEnvironmentContextProvider from '../../capabilities/contextStore/EnvironmentContext/useEnvironmentContextProvider';
import handleSessionInitializerError from '../../utils/session/handleSessionInitializerError/handleSessionInitializerError';
import { getConfigMode } from '../../utils/session/selectors';
import ErrorCard from '../../components/ErrorCard/ErrorCard';
import { ROOT_PATHS } from '../../unified-router/constants';
import useServiceContextProvider from '../../capabilities/contextStore/ServiceContext/useServiceContextProvider';
import PciSessionContextUpdateApi from '../../services/pciSessionContext/PciSessionContextUpdateApi';

import useParseCheckoutSessionId from './useParseCheckoutSessionId';

type InitializeSessionDataProps = {
  checkoutSessionId?: string;
  validateCheckoutSessionId?: boolean;
};

const InitializeSessionData: FC<
  PropsWithChildren<InitializeSessionDataProps>
> = ({
  checkoutSessionId: sessionId,
  children,
  validateCheckoutSessionId = true,
}) => {
  const appContext = useContext(AppContext);
  const parseCheckoutSessionId = useParseCheckoutSessionId();
  const { setAppearance } = useContext(CCGThemeProviderContext);
  const environmentContextState =
    useEnvironmentContextProvider();
  const { sessionStorageService } = useServiceContextProvider();

  const {
    initializeSessionError,
    initializeSessionException,
    setData,
    setInitializeSessionException,
    setInitializeSessionError,
  } = appContext;
  const [isLoading, setIsLoading] = useState(true);
  const checkoutSessionId = sessionId || parseCheckoutSessionId;

  const {
    containerConfig,
    postMessage,
    setEnvironmentState,
    appEnvironmentType,
  } = environmentContextState;

  useEffect(() => {
    let isMounted = true;
    if (checkoutSessionId) {
      getCheckoutSessionSettingsFromCommonCheckoutApi({
        checkoutSessionId,
        fetchConfig: true,
      })
        .then(async (data) => {
          const checkoutSessionData =
            data.originalCheckoutSessionResponse;
          const configMode = getConfigMode(checkoutSessionData);

          if (
            (!containerConfig ||
              containerConfig?.type === 'modal' ||
              containerConfig?.type === 'drawer') &&
            configMode === 'PAYMENT_METHOD_DISPLAY'
          ) {
            throw new Error(
              'Invalid configuration for this environment',
            );
          }

          if (
            isSessionComplete(
              data.originalCheckoutSessionResponse,
            ) &&
            Object.keys(ROOT_PATHS).includes(configMode)
          ) {
            setInitializeSessionError({
              title: 'SESSION_ALREADY_PROCESSED',
              detail: 'Session already processed',
              status: SESSION_ALREADY_PROCESSED_HTTP_STATUS,
            });
            return;
          }

          const serverUiModes =
            data.originalCheckoutSessionResponse.checkoutSession
              .checkoutRequest.config?.modes;

          const {
            customerId,
            merchantId,
            hcpToken,
            vendorPlatformKey,
            appearance,
          } = data;

          const upstreamEnv = await settingsApi.getUpstreamEnv();
          const headers = createDefaultHeader({
            upstreamEnv,
            merchantId,
            customerId: customerId || GUEST,
            checkoutSessionId,
            hcpToken,
          });

          setHeaders(headers);

          // Check if page was reloaded due to language change and reset PCI flag
          // This must be done AFTER headers are set for proper authentication
          const wasLanguageChanged =
            sessionStorageService.getItem(
              'reloadBecauseOfLanguageChange',
            );

          if (wasLanguageChanged === 'true') {
            try {
              // Reset PCI flag to false when language change causes page refresh
              // This ensures that subsequent GET /sessions/[session-id] calls return pci.active = false
              await PciSessionContextUpdateApi.updatePciSessionContext(
                {
                  sessionId: checkoutSessionId,
                  payload: {
                    operation: 'UPDATE_SESSION_PCI_FLAG',
                    sessionContext: {
                      pci: { active: false },
                    },
                  },
                },
              );
            } catch (error) {
              // Log error but don't fail the initialization
              console.error(
                'Failed to update PCI session context after language change:',
                error,
              );
            }
          }

          setData({
            ...data,
            vendorPlatformKey,
            uiModes: normalizeUiModesFromServer(serverUiModes),
            initialSessionData:
              data.originalCheckoutSessionResponse,
          });

          if (appearance) {
            setAppearance(appearance);
          }
          logWidgetUsageStatsEvents({
            appVersion: getVersion(),
            distributionType: getDistributionType(),
            merchantId,
          });
        })
        .catch((e: unknown) => {
          if (
            axios.isAxiosError(e) &&
            e.response?.data &&
            isHttpErrorResponse(e.response.data)
          ) {
            setInitializeSessionError(e.response.data);
          } else {
            setInitializeSessionException(
              ensureIsInstanceOfError(e),
            );
          }
        })
        .finally(() => {
          if (isMounted) {
            // This should happen once all other state is set, and requests are completed:
            setData({
              checkoutSessionId,
              hasInitialSessionDataLoaded: true,
              overlayLoaderConfig: { show: false },
            });
            setIsLoading(false);
          }
        });
    } else if (
      !checkoutSessionId &&
      containerConfig &&
      !validateCheckoutSessionId
    ) {
      /** @note: restrict this for embedded only */
      console.warn('Missing `checkoutSessionId`');
      setIsLoading(false);
      setData({ overlayLoaderConfig: { show: false } });
      /** @note: status code will be remove later when emitting error to callbacks
       *  this is to fulfill QA ask to remove status on invalid session errors in
       *  embedded and to work around typing errors related to status code.
       */
      setInitializeSessionError({
        title: 'INVALID_SESSION',
        detail: 'session id is invalid',
        status: SESSSION_ID_INVALID_STATUS_CODE,
      });
    }

    return () => {
      isMounted = false;
    };
  }, [checkoutSessionId, containerConfig]);

  /** @note: this useEffect handles embedded flow initialization failures */
  useEffect(() => {
    if (
      (initializeSessionError || initializeSessionException) &&
      containerConfig
    ) {
      /** @note: passing environmentContextState as a whole causes infinite
       *  loop. Need to separate the values and pass them individually.
       */
      handleSessionInitializerError({
        initializeSessionError,
        initializeSessionException,
        postMessage,
        containerConfig,
        setEnvironmentState,
        appEnvironmentType,
      });
    }
  }, [
    initializeSessionError,
    initializeSessionException,
    containerConfig,
  ]);

  /** @note: this useEffect handles hosted flow invalid checkout session state update */
  useEffect(() => {
    if (!checkoutSessionId && validateCheckoutSessionId) {
      console.warn('Missing `checkoutSessionId`');
      setData({
        hasInitialSessionDataLoaded: true,
      });
    }
  }, [checkoutSessionId, validateCheckoutSessionId]);

  /** @note: this code block handles hosted flow invalid checkout session failures */
  if (!checkoutSessionId && validateCheckoutSessionId) {
    return <ErrorCard />;
  }

  return (
    <>{checkoutSessionId && (!isLoading ? children : null)}</>
  );
};

export default InitializeSessionData;
